/**
 * PhotoQuestion — per-question photo capture for a `photo_ref` question (web).
 * Camera-capture ONLY (no file uploads). Supports single or multiple photos up
 * to `maxCount`, compressing each to under `maxMb` via CameraCapture. The stored
 * answer is a JSON array of photo ids.
 */
import React, { useState } from 'react';
import { Camera, Check, X } from 'lucide-react';

import { CameraCapture } from '@/components/CameraCapture';

const parseIds = (v: string): string[] => {
  if (!v) return [];
  try { const a = JSON.parse(v); return Array.isArray(a) ? a.filter(Boolean) : (v ? [v] : []); } catch { return [v]; }
};

export const PhotoQuestion: React.FC<{
  instructionId?: string; token?: string; questionCode: string; caption: string;
  value: string; onChange: (v: string) => void; testId: string;
  multiple: boolean; maxCount: number; maxMb: number;
  facing?: 'any' | 'front' | 'back'; allowSwitch?: boolean;
}> = ({ instructionId, token, questionCode, caption, value, onChange, testId, multiple, maxCount, maxMb, facing = 'any', allowSwitch = true }) => {
  const ids = parseIds(value);
  const cap = multiple ? maxCount : 1;
  const [capturing, setCapturing] = useState(ids.length === 0);
  const [captureKey, setCaptureKey] = useState(0);
  const canAdd = ids.length < cap;

  const emit = (arr: string[]): void => onChange(arr.length ? JSON.stringify(arr) : '');
  const onCaptured = (id: string): void => {
    emit([...ids, id]);
    setCapturing(false);
  };
  const remove = (id: string): void => {
    emit(ids.filter((x) => x !== id));
  };
  const startCapture = (): void => { setCaptureKey((k) => k + 1); setCapturing(true); };

  return (
    <div className="grid gap-2" data-testid={testId}>
      {ids.length > 0 && (
        <div className="grid gap-1.5">
          {ids.map((id, i) => (
            <div key={id} className="flex items-center gap-2 rounded-lg border border-border bg-white px-3 py-2" data-testid={`${testId}-photo-${id}`}>
              <Check size={15} className="text-green-600 shrink-0" />
              <span className="text-sm text-raptor-navy flex-1">Photo {i + 1} captured</span>
              <button type="button" onClick={() => remove(id)} className="text-raptor-navy/40 hover:text-red-500" data-testid={`${testId}-remove-${id}`}>
                <X size={15} />
              </button>
            </div>
          ))}
        </div>
      )}
      {capturing && canAdd ? (
        <CameraCapture key={captureKey} instructionId={instructionId as string} token={token} questionCode={questionCode}
          caption={caption} value="" onChange={onCaptured} testId={`${testId}-cam`} maxMb={maxMb} facing={facing} allowSwitch={allowSwitch} />
      ) : canAdd ? (
        <button type="button" onClick={startCapture}
          className="flex items-center justify-center gap-2 rounded-lg border-2 border-dashed border-border px-3 py-3 text-sm text-raptor-navy/70 hover:border-raptor-blue/50 hover:text-raptor-navy"
          data-testid={`${testId}-add`}>
          <Camera size={15} /> {multiple ? `Take photo (${ids.length}/${maxCount})` : 'Take photo'}
        </button>
      ) : !multiple && ids.length > 0 ? null : (
        <div className="text-[11px] text-raptor-navy/50">Maximum {maxCount} photos reached.</div>
      )}
    </div>
  );
};

export default PhotoQuestion;
